/**
 * Overlay queue client (Alerts brief §13, §14).
 *
 * The client an OBS Browser Source runs. It carries exactly one credential —
 * the output token — and exposes exactly three operations: ask what should be
 * showing, and say a shown alert started or finished. Nothing here decides
 * WHICH alert to show or for how long; that is `pendingAlertsForOutput` and
 * `claimNextAlert` server-side (apps/api/src/routes/overlay.ts). Polling this
 * client is itself what advances the queue.
 *
 * Failure handling mirrors the server's own two categories:
 *
 *   - 401 is TERMINAL. A revoked or rotated token will never start working
 *     again, so this stops polling for good rather than hammering an
 *     unattended browser source's only server forever.
 *   - Everything else (500, a network drop, 502/503) is TRANSIENT — OBS may
 *     have started before the API finished deploying — so the client stays
 *     usable and the caller is expected to try again.
 */

const API_PREFIX = '/api/v1';

export type AlertStatus = 'QUEUED' | 'ACTIVE' | 'COMPLETED';

/** What the overlay is told about one alert. Matches `AlertInstanceView`. */
export type AlertView = {
  id: string;
  status: AlertStatus;
  text: string;
  durationMs: number;
  queuedAt: string;
  synthetic: boolean;
  /**
   * The owning definition's appearance (`AlertStyle`, `packages/alerts/src/
   * style.ts`), untrusted JSON straight out of a database column. `AlertRender`
   * is what reads it defensively — this client just carries it through.
   */
  style: Record<string, unknown>;
};

/**
 * What `GET /overlay/alerts/pending` returns: the queue, and the output's
 * layout (`AlertLayout`) beside it. `layout` is output-wide rather than
 * per-alert, which is why it sits next to `alerts` instead of inside each one.
 */
export type PendingAlerts = { alerts: AlertView[]; layout: Record<string, unknown> };

/**
 * The wire shape, as it actually arrives — `layout` OPTIONAL, unlike
 * `PendingAlerts` above. Kept as a distinct type, not a cast to `PendingAlerts`
 * itself, so `layout`'s fallback below is a real defensive read rather than
 * one the type checker (and `no-unnecessary-condition`) can see is dead.
 */
type PendingAlertsResponse = { alerts: AlertView[]; layout?: Record<string, unknown> };

/** The `fetch` shape, injected so tests never touch the network. */
export type FetchImpl = typeof fetch;

export type QueueClient = {
  /** Fetches (and, server-side, advances) the queue for this output. */
  poll: () => Promise<PendingAlerts>;
  /** `phase: 'started'` — this instance is now on screen. */
  ackStarted: (id: string) => Promise<void>;
  /** `phase: 'completed'` — this instance has finished its run. */
  ackCompleted: (id: string) => Promise<void>;
  /**
   * True once a 401 has been seen. Terminal: the client will refuse every
   * further call rather than retry a token the server has already rejected.
   */
  readonly stopped: boolean;
};

export type CreateQueueClientOptions = {
  token: string;
  /** Defaults to the global `fetch`; overridden in tests. */
  fetchImpl?: FetchImpl;
};

/**
 * Thrown for a terminal (401) failure. The wording matches
 * `OutputTokenError` on the API (apps/api/src/plugins/output-token.ts) —
 * the same fact, told the same way, whichever side notices it.
 */
export class OverlayTokenInvalidError extends Error {
  constructor() {
    super('This overlay link is not valid.');
    this.name = 'OverlayTokenInvalidError';
  }
}

/** A non-401 failure: worth surfacing, but the caller should just try again. */
export class OverlayRequestFailedError extends Error {
  constructor(status: number) {
    super(`The overlay request failed (status ${String(status)}). Trying again.`);
    this.name = 'OverlayRequestFailedError';
  }
}

function withToken(path: string, token: string): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${API_PREFIX}${path}${separator}k=${encodeURIComponent(token)}`;
}

export function createQueueClient(options: CreateQueueClientOptions): QueueClient {
  const { token } = options;
  const fetchImpl = options.fetchImpl ?? fetch;
  let stopped = false;

  async function send(path: string, init?: RequestInit): Promise<Response> {
    // Once stopped, every call fails the same way without touching the
    // network — retrying a rejected token would just be noise against a
    // server that has already answered.
    if (stopped) {
      throw new OverlayTokenInvalidError();
    }

    const response = await fetchImpl(withToken(path, token), init);

    if (response.status === 401) {
      stopped = true;
      throw new OverlayTokenInvalidError();
    }

    if (!response.ok) {
      throw new OverlayRequestFailedError(response.status);
    }

    return response;
  }

  async function ack(id: string, phase: 'started' | 'completed'): Promise<void> {
    await send(`/overlay/alerts/${encodeURIComponent(id)}/ack`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ phase }),
    });
  }

  return {
    get stopped() {
      return stopped;
    },

    async poll(): Promise<PendingAlerts> {
      const response = await send('/overlay/alerts/pending');
      const payload = (await response.json()) as PendingAlertsResponse;
      return { alerts: payload.alerts, layout: payload.layout ?? {} };
    },

    ackStarted: (id: string) => ack(id, 'started'),
    ackCompleted: (id: string) => ack(id, 'completed'),
  };
}
